import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import DropCursor from '@tiptap/extension-dropcursor';
import suggestion from '../suggestion';
import CustomCodeBlockLowlight from '../codeBlock';
import lowlight from '../codeBlock/lowlight';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Strike from '@tiptap/extension-strike';
import ListItem from '@tiptap/extension-list-item';
import Blockquote from '@tiptap/extension-blockquote';
import HardBreak from '@tiptap/extension-hard-break';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import History from '@tiptap/extension-history';
import Heading from '@tiptap/extension-heading';
import Details from '@tiptap/extension-details';
import DetailsSummary from '@tiptap/extension-details-summary';
import DetailsContent from '@tiptap/extension-details-content';
import Code from '@tiptap/extension-code';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Typography from '@tiptap/extension-typography';
import { CustomLink } from '../customLink';
import Commands from '../slashCommand/command';
import { useCallback, useMemo, useState } from 'react';
import { ProjectLinkEmbed } from '../projectLinkEmbed';
import { SnapEmbed } from '../snapEmbed';
import { FigmaEmbed } from '../figmaEmbed';
import { LightshotEmbed } from '../lightShotEmbed';
import { YouTubeEmbed } from '../youtubeEmbed';
import { TaskLinkEmbed } from '../taskLinkEmbed';
import { DocumentEmbed } from '../documentEmbed';
import { ImageUploadEmbed } from '../imageUploadEmbed';
import { useEmbedPlugin } from './useEmbedPlugin';
import { rootStore } from '../../../mobx/rootStore';
import { DEFAULT_UPLOAD_FOLDER } from '../../../helpers/common';
import { CommentFilesInterface } from '../../../interfaces/TaskInterface';
import { ColorHighlighter } from '../colorHighlighter';
import { SmilieReplacer } from '../smilieReplacer';
import slashSuggestion from '../slashCommand/suggestion';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { Extension } from '@tiptap/react';

const CommandEnterShortcut = Extension.create({
  name: 'commandEnterShortcut',
  addKeyboardShortcuts() {
    return {
      'Mod-Enter': () => {
        return true;
      }
    };
  }
});

const DEFAULT_HANDLE_FILE_READ = () => Promise.resolve();


export interface UseTiptapExtensionsProps {
  placeholder?: string;
  onEmbedDetect?: (url: string) => void;
  isEditable?: boolean;
  setLoading?: (value: boolean) => void;
  setStateLoading?: (value: boolean) => void;
  addUploadedFiles?: (files: CommentFilesInterface[]) => void;
  recordSnapModalOpen?: () => void;
  handleFileInput?: () => void;
  handleGifModal?: () => void;
  editorInstanceIdRef?: React.MutableRefObject<string>;
  handleFileRead?: (file: File) => Promise<unknown>;
  /** Storage sub-folder for this editor's uploads. Defaults to `files/`. */
  uploadFolder?: string;
}

export const useTiptapExtensions = (props: UseTiptapExtensionsProps) => {
  const {
    placeholder,
    isEditable,
    setLoading,
    setStateLoading,
    addUploadedFiles,
    recordSnapModalOpen,
    handleFileInput,
    handleGifModal,
    editorInstanceIdRef,
    handleFileRead,
    uploadFolder = DEFAULT_UPLOAD_FOLDER
  } = props;

  // Mobx store variables
  const { appStore, settingStore } = rootStore;
  const { themeMode } = appStore || {};
  const { settingIntegration } = settingStore || {};
  const [isAllowForFigmaPreview, setIsAllowForFigmaPreview] = useState<boolean>(true);

  const { createEmbedPlugin } = useEmbedPlugin({
    settingIntegration,
    isAllowForFigmaPreview,
    setIsAllowForFigmaPreview,
    setLoading,
    setStateLoading,
    addUploadedFiles,
    themeMode,
    editorInstanceId: editorInstanceIdRef?.current,
    handleFileRead: handleFileRead ?? DEFAULT_HANDLE_FILE_READ,
    uploadFolder
  });


  const recordSnap = useCallback(() => {
    if (recordSnapModalOpen) recordSnapModalOpen();
  }, [recordSnapModalOpen]);

  const openGifModal = useCallback(() => {
    if (handleGifModal) handleGifModal();
  }, [handleGifModal]);

  return useMemo(
    () => [
      Underline,
      Placeholder.configure({ placeholder }),
      DropCursor.configure({
        color: 'var(--border-secondary)',
        width: 2
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention'
        },
        suggestion
      }),
      Bold,
      Italic,
      Strike,
      ListItem,
      Blockquote,
      HardBreak,
      HorizontalRule,
      History,
      Heading.configure({
        levels: [1, 2, 3, 4, 5, 6]
      }),
      CustomCodeBlockLowlight.configure({
        lowlight,
        isRead: !isEditable
      }),
      CustomLink.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https'
      }),
      SnapEmbed,
      FigmaEmbed,
      LightshotEmbed,
      YouTubeEmbed,
      TaskLinkEmbed,
      DocumentEmbed,
      ImageUploadEmbed,
      createEmbedPlugin(),
      Typography,
      ColorHighlighter,
      SmilieReplacer,
      Code,
      Document,
      Paragraph,
      Text,
      BulletList.configure({
        keepMarks: true,
        keepAttributes: false
      }),
      OrderedList.configure({
        keepMarks: true,
        keepAttributes: false
      }),
      Commands.configure({
        suggestion: slashSuggestion({ recordSnap, openGifModal, handleFileInput })
      }),
      Table.configure({
        resizable: true
      }),
      TableRow,
      TableCell,
      TableHeader,
      TaskList,
      TaskItem.configure({
        nested: true
      }),
      Details.extend({
        addAttributes() {
          return {
            open: {
              default: true,
              parseHTML: (element: HTMLElement) => element.hasAttribute('open'),
              renderHTML: ({ open }: { open: boolean }) => {
                if (!open) {
                  return {};
                }
                return { open: '' };
              }
            }
          };
        }
      }).configure({
        persist: true,
        HTMLAttributes: {
          class: 'details'
        }
      }),
      DetailsSummary.configure({
        HTMLAttributes: {
          'data-placeholder': 'Toggle title...'
        }
      }),
      DetailsContent.configure({
        HTMLAttributes: {
          'data-placeholder': 'Toggle content...'
        }
      }),
       CommandEnterShortcut,
      ProjectLinkEmbed
    ],
    [
      createEmbedPlugin,
      placeholder,
      handleFileInput,
      isEditable,
      openGifModal,
      recordSnap
    ]
  );
};
